<template>
    <div class="message-search">
        <!-- Arama Kutusu -->
        <a-input-search
            v-model="searchQuery"
            size="large"
            placeholder="Mesajlarda ara..."
            :loading="isLoading"
            @search="handleSearch"
            @change="handleInputChange"
            allow-clear
        />

        <!-- Loading Durumu -->
        <div v-if="isLoading" class="loading-container">
            <a-spin size="large" />
        </div>

        <!-- Sonuçlar -->
        <div v-else-if="groupedResults.length" class="search-results">
            <div v-for="group in groupedResults" :key="group.visitorId" class="result-group">
                <!-- Ziyaretçi Bilgisi -->
                <div class="visitor-info">
                    <div class="visitor-header">
                        <h3>{{ group.visitorName || "İsimsiz Ziyaretçi" }}</h3>
                        <SupsisButton icon="person" xxsmall @click="viewContactProfile(group.visitorId)" />
                    </div>
                    <span class="message-count">{{ group.messages.length }} mesaj</span>
                </div>

                <!-- Mesajlar -->
                <div class="messages">
                    <div
                        v-for="message in group.messages"
                        :key="message._id"
                        class="message-item"
                        @click="openChatHistory(message.conversation._id, message.visitor._id)"
                        :data-id="message._id"
                        :data-visitor-id="message.visitor._id"
                        :data-conversation-id="message.conversation._id"
                    >
                        <div class="message-header">
                            <div class="sender-info">
                                <AgentAvatar
                                    v-if="message.messageType.includes('user')"
                                    :userID="message.user"
                                    :size="35"
                                    :fontSize="12"
                                />
                                <ContactAvatar v-else :contact="message.visitor" size="smallest" />
                                <span class="sender-name">
                                    {{ getSenderName(message) }}
                                </span>
                            </div>
                            <div class="message-actions">
                                <span class="message-date">{{ formatDate(message.createdDate) }}</span>
                                <a-button
                                    type="primary"
                                    size="small"
                                    @click.stop="openChatHistory(message.conversation._id, message.visitor._id)"
                                >
                                    {{ $t("message.openChatFromSearch") }}
                                </a-button>
                            </div>
                        </div>
                        <div class="message-content">
                            <p v-html="$sanitize(message.description)"></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sonuç Bulunamadı -->
        <div v-else-if="searched" class="no-results">
            <a-empty :description="$t('message.noSearchResultsDescription')" />
        </div>

        <!-- UserChatHistoryWidget komponenti -->
        <UserChatHistoryWidget
            v-if="selectedVisitorId"
            :visitor-id="selectedVisitorId"
            :conversation-id="selectedConversationId"
            @close="closeChatHistory"
        />
    </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import { searchMessages } from "../../api/chat";
import { mapActions, mapState } from "vuex";
import UserChatHistoryWidget from "../UserChatHistoryWidget.vue";
import DOMPurify from "dompurify";
import AgentAvatar from "../common/AgentAvatar.vue";
import ContactAvatar from "../popup-chat/chat-window/ContactAvatar.vue";
import SupsisButton from "../../views/Pages/Settings/components/SupsisButton.vue";

export default {
    name: "MessageSearch",
    props: {
        defaultQuery: {
            type: String,
            default: "",
        },
    },
    components: {
        UserChatHistoryWidget,
        AgentAvatar,
        ContactAvatar,
        SupsisButton,
    },
    data() {
        return {
            searchQuery: this.defaultQuery,
            isLoading: false,
            searchResults: [],
            searched: false,

            selectedVisitorId: null,
            selectedConversationId: null,
        };
    },
    mounted() {
        if (this.searchQuery) {
            this.handleSearch();
        }
    },

    computed: {
        groupedResults() {
            return _(this.searchResults)
                .groupBy("visitor._id")
                .map((messages) => ({
                    visitorId: messages[0].visitor._id,
                    visitorName: messages[0].visitor.fullname,
                    messages: _.orderBy(messages, ["createdDate"], ["desc"]),
                }))
                .value();
        },
        debounceSearch() {
            return _.debounce(this.handleSearch, 500);
        },
        ...mapState({
            users: (state) => state.user.users.data,
        }),
    },

    methods: {
        async handleSearch() {
            if (!this.searchQuery.trim()) return;

            this.isLoading = true;
            this.searched = true;

            try {
                const results = await searchMessages(this.searchQuery);
                this.searchResults = results;
            } catch (error) {
                this.$message.error("Arama sırasında bir hata oluştu");
                console.error("Search error:", error);
                this.searchResults = [];
            } finally {
                this.isLoading = false;
            }
        },

        handleInputChange() {
            this.debounceSearch();
        },

        formatDate(date) {
            return moment(date).format("DD.MM.YYYY HH:mm");
        },

        openChatHistory(conversationId, visitorId) {
            this.selectedConversationId = conversationId;
            this.selectedVisitorId = visitorId;
            this.toggleChatHistory(true);
        },
        closeChatHistory() {
            this.selectedConversationId = null;
            this.selectedVisitorId = null;
        },

        ...mapActions({
            toggleChatHistory: "chat/toggleChatHistory",
            addNewWindow: "app/addNewWindow",
        }),

        getSenderName(message) {
            if (message.user) {
                const user = this.users.find((u) => u._id === message.user);
                return user ? user.name : "Temsilci";
            }
            return message.visitor.fullname || "Ziyaretçi";
        },

        $sanitize(html) {
            return DOMPurify.sanitize(html);
        },

        viewContactProfile(visitorId) {
            this.addNewWindow({
                type: "show-visitor",
                title: this.groupedResults.find((g) => g.visitorId === visitorId)?.visitorName || "Ziyaretçi Profili",
                payload: {
                    visitorId: visitorId,
                },
                footer: false,
                zIndex: 1000,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.message-search {
    padding: 20px;

    .loading-container {
        display: flex;
        justify-content: center;
        margin: 40px 0;
    }

    .search-results {
        margin-top: 20px;

        .result-group {
            background: #fff;
            border-radius: 4px;
            margin-bottom: 16px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

            .visitor-info {
                padding: 12px 16px;
                border-bottom: 1px solid #f0f0f0;
                display: flex;
                justify-content: space-between;
                align-items: center;

                .visitor-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    h3 {
                        margin: 0;
                        font-size: 16px;
                    }
                }

                .message-count {
                    color: #999;
                    font-size: 14px;
                }
            }

            .messages {
                .message-item {
                    padding: 12px 16px;
                    border-bottom: 1px solid #f0f0f0;
                    cursor: pointer;
                    transition: background-color 0.2s ease;

                    &:hover {
                        background-color: #f9f9f9;
                    }

                    &:last-child {
                        border-bottom: none;
                    }

                    .message-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 12px;

                        .sender-info {
                            display: flex;
                            align-items: center;
                            gap: 12px;

                            .sender-name {
                                font-weight: 500;
                                color: #333;
                                font-size: 14px;
                            }
                        }

                        .message-actions {
                            display: flex;
                            align-items: center;
                            gap: 16px;

                            .message-date {
                                font-size: 12px;
                                color: #999;
                            }
                        }
                    }

                    .message-content {
                        padding-left: 47px; // 35px (avatar) + 12px (gap)

                        p {
                            margin: 0;
                            line-height: 1.5;
                            color: #595959;
                            font-size: 14px;

                            ::v-deep(*) {
                                max-width: 100%;
                            }
                        }
                    }
                }
            }
        }
    }

    .no-results {
        margin-top: 40px;
    }

    .message-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 8px;
    }

    .messages {
        .message-item {
            .message-content {
                .message-date {
                    font-size: 12px;
                    color: #999;
                }
            }
        }
    }
}
</style>
